import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadUnionRegistrationState } from '../../union-registration/api/unionRegistrationApi.js';
import { canManageUnion } from '../../../shared/domain/unionAccess.js';

const NOTICE_ERRORS = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '유니온 관리자만 공지를 변경할 수 있습니다.',
  union_notice_not_found: '댓글을 작성할 공지를 찾을 수 없습니다.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function noticeError(error, fallback) {
  const raw = String(error?.message || '');
  const code = Object.keys(NOTICE_ERRORS).find((key) => raw.includes(key));
  let message = NOTICE_ERRORS[code] || raw || fallback || '공지 정보를 처리하지 못했습니다.';
  if (/permission denied for table union_notices/i.test(raw)) {
    message = '공지 테이블 권한 SQL이 아직 적용되지 않았습니다. 최신 Supabase 마이그레이션을 실행해주세요.';
  } else if (/permission denied for table union_notice_comments/i.test(raw)) {
    message = '공지 댓글 권한 SQL이 아직 적용되지 않았습니다. 최신 Supabase 마이그레이션을 실행해주세요.';
  }
  const result = new Error(message);
  result.code = code || error?.code || 'unknown_error';
  return result;
}

function isMissingCommentTable(error) {
  const raw = String(error?.message || '');
  return error?.code === '42P01' || /relation .*union_notice_comments.* does not exist/i.test(raw);
}

async function currentUnionContext() {
  const state = await loadUnionRegistrationState();
  if (state.membership?.status !== 'active' || state.union?.registration_status !== 'active' || !state.union?.id) {
    throw noticeError({ message: 'active_union_membership_required' });
  }
  return {
    unionId: String(state.union.id),
    unionName: String(state.union.name || ''),
    viewerRole: String(state.membership.role || ''),
    viewerUserId: String(state.membership.user_id || ''),
    viewerNickname: String(state.profile?.nickname || '멤버'),
  };
}

function normalizeComment(row) {
  return {
    id: String(row?.id || ''),
    noticeId: String(row?.notice_id || ''),
    authorUserId: String(row?.author_user_id || ''),
    authorName: String(row?.author_name || '멤버'),
    body: String(row?.body || ''),
    createdAt: row?.created_at || null,
    updatedAt: row?.updated_at || null,
  };
}

function normalizeNotice(row, comments = []) {
  return {
    id: String(row?.id || ''),
    title: String(row?.title || ''),
    body: String(row?.body || ''),
    pinned: Boolean(row?.pinned),
    createdAt: row?.created_at || null,
    updatedAt: row?.updated_at || null,
    comments,
  };
}

export async function loadUnionNotices() {
  const client = requireClient();
  const context = await currentUnionContext();
  const [noticeResult, commentResult] = await Promise.all([
    client
      .from('union_notices')
      .select('id,union_id,title,body,pinned,created_at,updated_at')
      .eq('union_id', context.unionId)
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(100),
    client
      .from('union_notice_comments')
      .select('id,notice_id,union_id,author_user_id,author_name,body,created_at,updated_at')
      .eq('union_id', context.unionId)
      .order('created_at', { ascending: true })
      .limit(2000),
  ]);

  if (noticeResult.error) throw noticeError(noticeResult.error, '유니온 공지를 불러오지 못했습니다.');
  if (commentResult.error && !isMissingCommentTable(commentResult.error)) {
    throw noticeError(commentResult.error, '공지 댓글을 불러오지 못했습니다.');
  }

  const commentsByNotice = new Map();
  for (const row of commentResult.data || []) {
    const comment = normalizeComment(row);
    if (!commentsByNotice.has(comment.noticeId)) commentsByNotice.set(comment.noticeId, []);
    commentsByNotice.get(comment.noticeId).push(comment);
  }

  return {
    ...context,
    notices: (noticeResult.data || []).map((row) => normalizeNotice(row, commentsByNotice.get(String(row.id)) || [])),
  };
}

export async function saveUnionNotice({ id = '', title, body, pinned = false }) {
  const client = requireClient();
  const context = await currentUnionContext();
  if (!canManageUnion(context.viewerRole)) throw noticeError({ message: 'union_manager_required' });
  const cleanTitle = String(title || '').trim();
  const cleanBody = String(body || '').trim();
  if (!cleanTitle) throw new Error('공지 제목을 입력해주세요.');
  if (!cleanBody) throw new Error('공지 내용을 입력해주세요.');

  const writable = { title: cleanTitle, body: cleanBody, pinned: Boolean(pinned) };
  const query = id
    ? client.from('union_notices').update(writable).eq('id', String(id)).eq('union_id', context.unionId)
    : client.from('union_notices').insert({ union_id: context.unionId, ...writable });
  const { data, error } = await query.select('id,union_id,title,body,pinned,created_at,updated_at').limit(1).maybeSingle();
  if (error) throw noticeError(error, '공지를 저장하지 못했습니다.');
  if (!data?.id) throw new Error('공지 저장 결과를 확인하지 못했습니다.');
  return normalizeNotice(data);
}

export async function deleteUnionNotice(id) {
  const noticeId = String(id || '').trim();
  if (!noticeId) throw new Error('삭제할 공지를 찾을 수 없습니다.');
  const client = requireClient();
  const context = await currentUnionContext();
  if (!canManageUnion(context.viewerRole)) throw noticeError({ message: 'union_manager_required' });
  const { data, error } = await client
    .from('union_notices')
    .delete()
    .eq('id', noticeId)
    .eq('union_id', context.unionId)
    .select('id')
    .limit(1)
    .maybeSingle();
  if (error) throw noticeError(error, '공지를 삭제하지 못했습니다.');
  if (!data?.id) throw new Error('삭제할 공지를 찾을 수 없습니다.');
  return data.id;
}

export async function saveUnionNoticeComment({ id = '', noticeId, body }) {
  const targetNoticeId = String(noticeId || '').trim();
  const cleanBody = String(body || '').trim();
  if (!targetNoticeId) throw new Error('댓글을 작성할 공지를 찾을 수 없습니다.');
  if (!cleanBody) throw new Error('댓글 내용을 입력해주세요.');
  if (cleanBody.length > 1000) throw new Error('댓글은 1000자 이하로 입력해주세요.');

  const client = requireClient();
  const context = await currentUnionContext();
  const query = id
    ? client
      .from('union_notice_comments')
      .update({ body: cleanBody })
      .eq('id', String(id))
      .eq('notice_id', targetNoticeId)
      .eq('union_id', context.unionId)
    : client.from('union_notice_comments').insert({ notice_id: targetNoticeId, body: cleanBody });
  const { data, error } = await query
    .select('id,notice_id,union_id,author_user_id,author_name,body,created_at,updated_at')
    .limit(1)
    .maybeSingle();
  if (error) throw noticeError(error, '댓글을 저장하지 못했습니다.');
  if (!data?.id) throw new Error('댓글 저장 결과를 확인하지 못했습니다.');
  return normalizeComment(data);
}

export async function deleteUnionNoticeComment(id) {
  const commentId = String(id || '').trim();
  if (!commentId) throw new Error('삭제할 댓글을 찾을 수 없습니다.');
  const client = requireClient();
  const context = await currentUnionContext();
  const { data, error } = await client
    .from('union_notice_comments')
    .delete()
    .eq('id', commentId)
    .eq('union_id', context.unionId)
    .select('id')
    .limit(1)
    .maybeSingle();
  if (error) throw noticeError(error, '댓글을 삭제하지 못했습니다.');
  if (!data?.id) throw new Error('삭제할 댓글을 찾을 수 없거나 삭제 권한이 없습니다.');
  return data.id;
}
